import type {
  CheckInCommentDto,
  CheckInRuleSummaryDto,
  CreateCheckInCommentDto,
  CreateCheckInRuleDto,
  UpdateCheckInRuleDto,
} from '@schwrz/shared';
import { defineStore } from 'pinia';
import { ref } from 'vue';

import { api } from '../shared/api/client';

export const useCheckInsStore = defineStore('checkIns', () => {
  /** rules keyed by clientId */
  const byClient = ref(new Map<string, CheckInRuleSummaryDto[]>());
  /** single-rule cache (with full comments) */
  const ruleById = ref(new Map<string, CheckInRuleSummaryDto>());
  const commentsByRule = ref(new Map<string, CheckInCommentDto[]>());
  const loadingClients = ref(new Set<string>());

  function listOf(clientId: string): CheckInRuleSummaryDto[] {
    return byClient.value.get(clientId) ?? [];
  }

  function setListFor(clientId: string, rules: CheckInRuleSummaryDto[]): void {
    const next = new Map(byClient.value);
    next.set(clientId, rules);
    byClient.value = next;
  }

  function indexRule(rule: CheckInRuleSummaryDto): void {
    const next = new Map(ruleById.value);
    next.set(rule.id, rule);
    ruleById.value = next;
    // also update the list slot for clientId so the column stays consistent
    const list = listOf(rule.clientId);
    if (list.length > 0) {
      setListFor(
        rule.clientId,
        list.some((r) => r.id === rule.id)
          ? list.map((r) => (r.id === rule.id ? rule : r))
          : [...list, rule],
      );
    }
  }

  async function loadForClient(clientId: string, force = false): Promise<void> {
    if (loadingClients.value.has(clientId)) return;
    if (byClient.value.has(clientId) && !force) return;
    loadingClients.value.add(clientId);
    try {
      const res = await api<{ rules: CheckInRuleSummaryDto[] }>(
        `/api/clients/${encodeURIComponent(clientId)}/check-in-rules`,
      );
      setListFor(clientId, res.rules);
      for (const r of res.rules) {
        const next = new Map(ruleById.value);
        next.set(r.id, r);
        ruleById.value = next;
      }
    } finally {
      loadingClients.value.delete(clientId);
    }
  }

  async function fetchOne(ruleId: string): Promise<{
    rule: CheckInRuleSummaryDto;
    comments: CheckInCommentDto[];
  }> {
    const res = await api<{ rule: CheckInRuleSummaryDto; comments: CheckInCommentDto[] }>(
      `/api/check-in-rules/${encodeURIComponent(ruleId)}`,
    );
    indexRule(res.rule);
    const next = new Map(commentsByRule.value);
    next.set(ruleId, res.comments);
    commentsByRule.value = next;
    return res;
  }

  async function create(
    clientId: string,
    payload: CreateCheckInRuleDto,
  ): Promise<CheckInRuleSummaryDto> {
    const row = await api<CheckInRuleSummaryDto>(
      `/api/clients/${encodeURIComponent(clientId)}/check-in-rules`,
      { method: 'POST', body: payload },
    );
    indexRule(row);
    const list = listOf(clientId);
    setListFor(clientId, [...list, row]);
    return row;
  }

  async function update(
    ruleId: string,
    payload: UpdateCheckInRuleDto,
  ): Promise<CheckInRuleSummaryDto> {
    const row = await api<CheckInRuleSummaryDto>(`/api/check-in-rules/${ruleId}`, {
      method: 'PATCH',
      body: payload,
    });
    indexRule(row);
    return row;
  }

  async function remove(rule: CheckInRuleSummaryDto): Promise<void> {
    await api<void>(`/api/check-in-rules/${rule.id}`, { method: 'DELETE' });
    const map = new Map(ruleById.value);
    map.delete(rule.id);
    ruleById.value = map;
    const list = listOf(rule.clientId);
    setListFor(
      rule.clientId,
      list.filter((r) => r.id !== rule.id),
    );
    const c = new Map(commentsByRule.value);
    c.delete(rule.id);
    commentsByRule.value = c;
  }

  async function postComment(
    ruleId: string,
    payload: CreateCheckInCommentDto,
  ): Promise<CheckInCommentDto> {
    const row = await api<CheckInCommentDto>(
      `/api/check-in-rules/${encodeURIComponent(ruleId)}/comments`,
      { method: 'POST', body: payload },
    );
    const current = commentsByRule.value.get(ruleId) ?? [];
    const next = new Map(commentsByRule.value);
    next.set(ruleId, [...current, row]);
    commentsByRule.value = next;
    // bump comment count on the cached rule
    const rule = ruleById.value.get(ruleId);
    if (rule) {
      indexRule({ ...rule, commentCount: rule.commentCount + 1 });
    }
    return row;
  }

  async function removeComment(comment: CheckInCommentDto): Promise<void> {
    await api<void>(`/api/check-in-comments/${comment.id}`, { method: 'DELETE' });
    const current = commentsByRule.value.get(comment.ruleId) ?? [];
    const next = new Map(commentsByRule.value);
    next.set(
      comment.ruleId,
      current.filter((c) => c.id !== comment.id),
    );
    commentsByRule.value = next;
    const rule = ruleById.value.get(comment.ruleId);
    if (rule) indexRule({ ...rule, commentCount: Math.max(0, rule.commentCount - 1) });
  }

  return {
    byClient,
    ruleById,
    commentsByRule,
    loadingClients,
    listOf,
    loadForClient,
    fetchOne,
    create,
    update,
    remove,
    postComment,
    removeComment,
  };
});
